import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Avatar,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { formatRUT } from '../../utils/validations';
import { useAuth } from '../../context/AuthContext';
import { getUserProfile, updateUserProfile, UserProfile as IUserProfile } from '../../services/userProfile';

const ProfileCard = styled(Card)({
  borderRadius: '20px',
  boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
  border: '1px solid rgba(46, 90, 51, 0.1)',
});

const AvatarContainer = styled(Box)({
  position: 'relative',
  display: 'inline-block',
});

const CameraButton = styled(IconButton)({
  position: 'absolute',
  bottom: -5,
  right: -5,
  backgroundColor: '#2e5a33',
  color: 'white',
  width: 40,
  height: 40,
  '&:hover': {
    backgroundColor: '#1a3d1e',
  },
});

const CustomerProfile: React.FC = () => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [originalProfile, setOriginalProfile] = useState<IUserProfile | null>(null);
  const [profile, setProfile] = useState({
    nombre_completo: '',
    telefono: '',
    rut: '' as string | undefined,
    direccion: '' as string | undefined,
    fecha_nacimiento: '' as string | undefined,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        console.log('📥 Cargando perfil desde Django...');
        const data = await getUserProfile();
        console.log('✅ Perfil cargado:', data);
        
        setProfile({
          nombre_completo: data.nombre_completo || '',
          telefono: data.telefono || '',
          rut: data.rut || '',
          direccion: data.direccion || '',
          fecha_nacimiento: data.fecha_nacimiento || '',
        });
        setOriginalProfile(data);
      } catch (error) {
        console.error('❌ Error al cargar perfil:', error);
        setSnackbar({
          open: true,
          message: 'Error al cargar datos del perfil',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    // Restaurar datos originales
    if (originalProfile) {
      setProfile({
        nombre_completo: originalProfile.nombre_completo || '',
        telefono: originalProfile.telefono || '',
        rut: originalProfile.rut || '',
        direccion: originalProfile.direccion || '',
        fecha_nacimiento: originalProfile.fecha_nacimiento || '',
      });
    }
  };

  const handleSave = async () => {
    if (!user) return;

    // Validación de campos obligatorios
    if (!profile.nombre_completo || profile.nombre_completo.trim() === '') {
      setSnackbar({
        open: true,
        message: 'El nombre completo es obligatorio',
        severity: 'error'
      });
      return;
    }

    try {
      console.log('💾 Guardando perfil en Django...');
      const updatedProfile = await updateUserProfile(profile);
      console.log('✅ Perfil actualizado:', updatedProfile);
      
      setOriginalProfile(updatedProfile);
      setEditing(false);
      
      setSnackbar({
        open: true,
        message: '✅ Perfil actualizado exitosamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error al actualizar perfil:', error);
      setSnackbar({
        open: true,
        message: `❌ Error al actualizar el perfil`,
        severity: 'error'
      });
    }
  };

  const handleChange = (field: keyof typeof profile) => (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    
    // Formateo automático para RUT chileno
    if (field === 'rut') {
      value = formatRUT(value);
    }
    
    // Formateo automático para teléfono chileno
    if (field === 'telefono') {
      // Eliminar todo excepto números
      const numbers = value.replace(/\D/g, '');
      
      // Si está vacío, permitir
      if (numbers.length === 0) {
        value = '';
      } else if (numbers.startsWith('56')) {
        // Si ya empieza con 56, formatear: +56 9 XXXX XXXX
        const phoneNumbers = numbers.substring(2);
        if (phoneNumbers.length === 0) {
          value = '+56 ';
        } else if (phoneNumbers.length <= 1) {
          value = `+56 ${phoneNumbers}`;
        } else if (phoneNumbers.length <= 5) {
          value = `+56 ${phoneNumbers.substring(0, 1)} ${phoneNumbers.substring(1)}`;
        } else {
          value = `+56 ${phoneNumbers.substring(0, 1)} ${phoneNumbers.substring(1, 5)} ${phoneNumbers.substring(5, 9)}`;
        }
      } else {
        // Si no empieza con 56, agregar +56 y formatear
        if (numbers.length <= 1) {
          value = `+56 ${numbers}`;
        } else if (numbers.length <= 5) {
          value = `+56 ${numbers.substring(0, 1)} ${numbers.substring(1)}`;
        } else {
          value = `+56 ${numbers.substring(0, 1)} ${numbers.substring(1, 5)} ${numbers.substring(5, 9)}`;
        }
      }
    }
    
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log('Nueva foto de perfil:', file);
      setSnackbar({
        open: true,
        message: 'Funcionalidad de cambio de avatar en desarrollo',
        severity: 'info'
      });
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Reciente';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#2e5a33' }} />
      </Box>
    );
  }

  return (
    <Box>
      <ProfileCard>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: '#2e5a33' }}>
            Información Personal
          </Typography>

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: 'center' }}>
              <AvatarContainer>
                <Avatar
                  src={originalProfile?.foto_perfil || ''}
                  alt={profile.nombre_completo}
                  sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                >
                  {profile.nombre_completo?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                {editing && (
                  <>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="avatar-upload"
                      type="file"
                      onChange={handleAvatarChange}
                    />
                    <label htmlFor="avatar-upload">
                      <CameraButton>
                        <PhotoCameraIcon fontSize="small" />
                      </CameraButton>
                    </label>
                  </>
                )}
              </AvatarContainer>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {profile.nombre_completo || 'Usuario'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Cliente desde {formatDate(originalProfile?.fecha_registro)}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Nombre completo"
                    value={profile.nombre_completo}
                    onChange={handleChange('nombre_completo')}
                    disabled={!editing}
                    variant={editing ? 'outlined' : 'filled'}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Correo electrónico"
                    value={user?.email || ''}
                    disabled
                    variant="filled"
                    helperText="No se puede modificar el email"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Teléfono móvil"
                    value={profile.telefono}
                    onChange={handleChange('telefono')}
                    disabled={!editing}
                    variant={editing ? 'outlined' : 'filled'}
                    placeholder="+56 9 XXXX XXXX"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="RUT"
                    value={profile.rut}
                    onChange={handleChange('rut')}
                    disabled={!editing}
                    variant={editing ? 'outlined' : 'filled'}
                    placeholder="12.345.678-9"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Fecha de nacimiento"
                    type="date"
                    value={profile.fecha_nacimiento}
                    onChange={handleChange('fecha_nacimiento')}
                    disabled={!editing}
                    variant={editing ? 'outlined' : 'filled'}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    value={profile.direccion}
                    onChange={handleChange('direccion')}
                    disabled={!editing}
                    variant={editing ? 'outlined' : 'filled'}
                    placeholder="Calle, número, comuna, ciudad"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            {editing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  sx={{ borderColor: '#f44336', color: '#f44336' }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{
                    background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
                    },
                  }}
                >
                  Guardar Cambios
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{
                  background: 'linear-gradient(45deg, #2e5a33, #4a7c59)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1a3d1e, #2e5a33)',
                  },
                }}
              >
                Editar Perfil
              </Button>
            )}
          </Box>
        </CardContent>
      </ProfileCard>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerProfile;
